import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Monitor, Smartphone, ExternalLink, Loader2, Edit3, MessageSquarePlus } from 'lucide-react';
import { getSite, Website } from '../api/website';
import { RequestChangesPanel } from '../components/RequestChangesPanel';

type ViewMode = 'desktop' | 'mobile';

export default function PreviewPage() {
  const [website, setWebsite] = useState<Website | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    async function fetchSite() {
      try {
        const { website } = await getSite();
        setWebsite(website);
      } catch (err) {
        console.error('Failed to load website:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSite();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (!website?.cloudflare_url) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No preview available</h2>
          <p className="text-gray-600 mb-4">Your website hasn't been deployed yet.</p>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-lg font-semibold text-gray-900">
                {website.business_name}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'desktop'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  <span className="hidden sm:inline">Desktop</span>
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'mobile'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  <span className="hidden sm:inline">Mobile</span>
                </button>
              </div>

              {/* Edit Site */}
              <Link
                to="/edit"
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit Site</span>
              </Link>

              {/* Open in New Tab */}
              <a
                href={website.cloudflare_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">Open Site</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Preview Frame */}
      <main className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <div
          className={`bg-white rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ${
            viewMode === 'desktop'
              ? 'w-full max-w-6xl h-[80vh]'
              : 'w-[375px] h-[667px]'
          }`}
        >
          {/* Browser Chrome (Desktop only) */}
          {viewMode === 'desktop' && (
            <div className="bg-gray-200 px-4 py-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-white rounded-md px-4 py-1 text-sm text-gray-500 truncate">
                  {website.cloudflare_url}
                </div>
              </div>
            </div>
          )}

          {/* Mobile Notch (Mobile only) */}
          {viewMode === 'mobile' && (
            <div className="bg-black px-6 py-2 flex items-center justify-center">
              <div className="w-32 h-6 bg-black rounded-full relative">
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gray-800" />
              </div>
            </div>
          )}

          {/* iframe */}
          <iframe
            src={website.cloudflare_url}
            className={`w-full bg-white ${
              viewMode === 'desktop' ? 'h-[calc(80vh-40px)]' : 'h-[calc(667px-40px)]'
            }`}
            title="Website Preview"
          />
        </div>
      </main>

      {/* Action Bar */}
      <footer className="bg-white border-t border-gray-200 sticky bottom-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <p className="text-sm text-gray-600">
              Need changes? Submit a revision request.
            </p>
            <button
              onClick={() => setIsPanelOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white font-medium rounded-lg transition-colors"
            >
              <MessageSquarePlus className="w-4 h-4" />
              Request Changes
            </button>
          </div>
        </div>
      </footer>

      {/* Request Changes Side Panel */}
      <RequestChangesPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
      />
    </div>
  );
}
